import type {
  Contact,
  ContactChannel,
  LastAdSnapshot,
  LastWebsiteVisitSnapshot,
} from "@/features/contact/types";
import type { Channel, ChannelType } from "@/types/channel";
import type { Lifecycle } from "@/types/lifecycle";
import type { Tag } from "@/types/tag";
import type { User } from "@/types/user";

import { faker } from "@faker-js/faker";

import {
  getMockMarket,
  getMockMarketProfile,
  type MockMarket,
} from "../market";

import {
  createAvatarUrl,
  createId,
  createPastTimestamp,
  createPhoneNumber,
  createPrice,
  createRecentTimestamp,
  generateLocalName,
  pickMultiple,
  pickRandom,
  randomBool,
  randomInt,
} from "./base";

import { ORIGINS } from "@/features/contact/constants/origin";
import { OPT_OUT_SOURCE } from "@/features/contact/types";

interface GenerateContactsParams {
  count: number;
  lifecycles: Lifecycle[];
  tags: Tag[];
  users: User[];
  channels: Channel[];
  isEcommerce?: boolean;
}

interface GenerateContactsResult {
  contacts: Contact[];
  contactLifecycleMap: Map<string, string>;
  contactTagMap: Map<string, string[]>;
}

/**
 * Contacts whose last inbound message is deliberately old, keyed by index, so
 * the closed-window composer is reachable.
 *
 * Every other contact is kept inside its window on purpose (see below), which
 * means the state an agent hits most often on a quiet queue could not be opened
 * in mock mode at all - not to look at, not to screenshot, not to test. These
 * two make it reachable: one per window length, since WhatsApp's is 24 hours
 * and Instagram's is seven days.
 */
export const STALE_CONTACTS: Record<number, ChannelType> = {
  4: "whatsapp",
  5: "instagram",
  6: "instagram",
  7: "whatsapp",
};

/**
 * Generate contact channels based on available channels
 */
function generateContactChannels(
  channels: Channel[],
  _contactId: string,
  index: number,
): ContactChannel[] {
  const staleType = STALE_CONTACTS[index];
  const stale = staleType
    ? channels.filter((channel) => channel.type === staleType)
    : [];

  // Each contact has 1-2 channels
  const selectedChannels =
    stale.length > 0 ? stale : pickMultiple(channels, 1, 2);

  // The contact↔channel link is keyed by the org channel's id (that's how the
  // composer matches the selected channel to a contact channel and resolves the
  // messaging window). A fresh id here would never match, so the composer would
  // treat every conversation as out-of-window and hide the input box.
  return selectedChannels.map((channel) => ({
    id: channel.id,
    type: channel.type,
    identifier:
      channel.type === "whatsapp"
        ? createPhoneNumber()
        : faker.internet.username(),
    // Keep the last inbound message inside the 24h window so the composer is
    // editable and messages can be sent without needing a template - except for
    // the contacts named above, which exist to show the opposite.
    // A fixed 45 days, not `createPastTimestamp(30)`: that helper returns a
    // date somewhere *within* the last 30 days, which lands inside Instagram's
    // seven-day window often enough that the figure came out wrong at random.
    lastMessageSentByUserAt:
      stale.length > 0
        ? new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString()
        : createRecentTimestamp(),
    lastCommentSentByUserAt: null,
    lastMessageSentAt: createPastTimestamp(1),
    lastMessageSentBy: pickRandom(["contact", "user"] as const),
  }));
}

const AD_NAMES = [
  "Summer Botox Campaign",
  "Filler Promo Q2",
  "New Patient Welcome Offer",
  "Lip Filler Awareness",
  "Hydrafacial Spring Drive",
  "Laser Hair Removal Bundle",
  "Skin Boosters Launch",
  "Anti-Wrinkle Retargeting",
];

const WEBSITE_NAMES: Record<
  MockMarket,
  Array<{ name: string; domain: string }>
> = {
  nepal: [
    { name: "Kathmandu Skin Clinic", domain: "kathmanduskin.com.np" },
    { name: "Himalayan Aesthetics", domain: "himalayanaesthetics.com.np" },
    { name: "Lalitpur Skin Studio", domain: "lalitpurskin.com.np" },
  ],
  uae: [
    { name: "Dubai Aesthetic Clinic", domain: "dubaiaesthetic.ae" },
    { name: "Jumeirah Glow Clinic", domain: "jumeirahglow.ae" },
    { name: "Noor Skin Studio", domain: "noorskin.ae" },
  ],
  usa: [
    { name: "Manhattan Aesthetic Clinic", domain: "manhattanaesthetic.com" },
    { name: "Glow Med Spa", domain: "glowmedspa.com" },
    { name: "Meridian Skin Studio", domain: "meridianskin.com" },
  ],
};

const INSURERS: Record<MockMarket, string[]> = {
  nepal: ["Nepal Insurance", "Himalayan Everest", "IME General", "Self-paying"],
  uae: ["Daman", "Sukoon", "Bupa", "Self-paying"],
  usa: ["Aetna", "Blue Cross", "Cigna", "Self-paying"],
};

const WEBSITE_PAGES: Array<{ path: string; title: string }> = [
  { path: "/pricing", title: "Pricing & Packages" },
  { path: "/treatments/botox", title: "Botox Treatments" },
  { path: "/treatments/filler", title: "Dermal Fillers" },
  { path: "/contact", title: "Contact Us" },
  { path: "/about", title: "About the Clinic" },
  { path: "/book", title: "Book a Consultation" },
];

function generateLastAd(): LastAdSnapshot {
  return {
    id: createId(),
    name: pickRandom(AD_NAMES),
    clickedAt: createPastTimestamp(randomInt(1, 5)),
  };
}

function generateLastWebsiteVisit(
  market: MockMarket,
): LastWebsiteVisitSnapshot {
  const site = pickRandom(WEBSITE_NAMES[market]);
  const page = pickRandom(WEBSITE_PAGES);

  return {
    websiteId: createId(),
    websiteName: site.name,
    domain: site.domain,
    url: `https://${site.domain}${page.path}`,
    pagePath: page.path,
    pageTitle: page.title,
    visitedAt: createPastTimestamp(randomInt(1, 3)),
  };
}

const soonTimestamp = (days: number) => faker.date.soon({ days }).toISOString();

// Weighted like a real book: most last visits happened, a few were missed or dropped.
const LAST_VISIT_STATUSES = [
  "completed",
  "completed",
  "completed",
  "completed",
  "no_show",
  "cancelled",
  "pending",
  "confirmed",
] as const;

function generateVisitFields(): Pick<
  Contact,
  "lastVisit" | "nextVisitAt" | "paidToDate"
> {
  if (randomBool(0.35)) {
    return {
      lastVisit: null,
      nextVisitAt: randomBool(0.3) ? soonTimestamp(30) : null,
      paidToDate: "0.00",
    };
  }
  const status = pickRandom([...LAST_VISIT_STATUSES]);

  return {
    lastVisit: { at: createPastTimestamp(randomInt(2, 180)), status },
    nextVisitAt: randomBool(0.4) ? soonTimestamp(30) : null,
    paidToDate:
      status === "completed" && randomBool(0.8)
        ? createPrice(100, 8000)
        : "0.00",
  };
}

const LAST_ORDER_STATUSES = [
  "delivered",
  "delivered",
  "delivered",
  "delivered",
  "out_for_delivery",
  "packaging",
  "pending",
  "delivery_failed",
  "returned",
  "on_hold",
] as const;

function generateOrderFields(): Pick<
  Contact,
  "ordersCount" | "lastOrder" | "spent"
> {
  const ordersCount = randomBool(0.45) ? 0 : randomInt(1, 8);

  if (ordersCount === 0) {
    // A cancelled order counts as none, but the row still says what happened.
    return {
      ordersCount,
      lastOrder: randomBool(0.15)
        ? { at: createPastTimestamp(randomInt(2, 90)), status: "cancelled" }
        : null,
      spent: "0.00",
    };
  }

  return {
    ordersCount,
    lastOrder: {
      at: createPastTimestamp(randomInt(1, 120)),
      status: pickRandom([...LAST_ORDER_STATUSES]),
    },
    spent: createPrice(50, 5000),
  };
}

/**
 * Generate mock contacts for a clinic or a store
 */
export function generateContacts(
  params: GenerateContactsParams,
): GenerateContactsResult {
  const {
    count,
    lifecycles,
    tags,
    users,
    channels,
    isEcommerce = false,
  } = params;
  const contacts: Contact[] = [];
  const contactLifecycleMap = new Map<string, string>();
  const contactTagMap = new Map<string, string[]>();
  const market = getMockMarket();
  const profile = getMockMarketProfile(market);

  // Distribute contacts across lifecycle stages (weighted)
  const lifecycleWeights = [15, 20, 25, 15, 20, 5]; // Percentages roughly

  for (let i = 0; i < count; i++) {
    const { firstName, lastName } = generateLocalName();
    const contactId = createId();

    // Assign lifecycle based on weighted distribution
    const lifecycleIndex = getWeightedLifecycleIndex(
      i,
      count,
      lifecycleWeights,
    );
    const lifecycle = lifecycles[lifecycleIndex] || lifecycles[0];

    // Assign 0-3 tags randomly
    const contactTags = pickMultiple(tags, 0, 3);

    // Assign to a user (70% of contacts are assigned)
    const assignedUser = randomBool(0.7) ? pickRandom(users) : null;

    // Determine conversation status. A stale contact is always left open: it
    // exists to be opened and looked at, and a random "closed" would hide it.
    const status =
      i in STALE_CONTACTS
        ? ("open" as const)
        : pickRandom(["open", "closed", "snoozed"] as const);
    const snoozedUntil =
      status === "snoozed" ? faker.date.soon({ days: 3 }).toISOString() : null;

    const createdAt = createPastTimestamp(randomInt(1, 60));
    const contactChannels = generateContactChannels(channels, contactId, i);

    const noteText = randomBool(0.4) ? faker.lorem.sentence() : null;
    const noteAt = noteText ? createPastTimestamp(randomInt(1, 3)) : null;

    // First contact gets the most recent message time (will appear first in sorted list)
    const isFirstContact = i === 0;
    // The stale contacts sort just behind it. The list is ordered by this and
    // paginated, so leaving them on a random past date meant they were often on
    // page two - present in the data, invisible to anyone looking for them.
    const lastMessageProcessedAt = isFirstContact
      ? new Date().toISOString() // Most recent - will be first when sorted
      : i in STALE_CONTACTS
        ? new Date(Date.now() - (1 + i) * 60 * 1000).toISOString()
        : createPastTimestamp(randomInt(1, 7)); // Others get random past times

    contacts.push({
      id: contactId,
      firstName,
      lastName,
      username: randomBool(0.5)
        ? faker.internet.username({ firstName, lastName })
        : null,
      email: randomBool(0.8)
        ? faker.internet.email({ firstName, lastName })
        : null,
      phone: randomBool(0.9) ? createPhoneNumber() : null,
      address: randomBool(0.6) ? faker.location.streetAddress(true) : null,
      language: profile.locale,
      country: randomBool(0.7) ? profile.region : null,
      imageUrl: createAvatarUrl(),
      // Two named contacts rather than a 2% chance: at 35 contacts that coin
      // usually came up zero, so the Blocked view had nothing in it to look at.
      blocked: i === 8 || i === 19,
      // Opted out by a reply and by a teammate, so the chip, the notice and
      // the audience row have something to show.
      optedOutAt: i === 5 || i === 13 ? createPastTimestamp(30) : null,
      optOutSource:
        i === 5
          ? OPT_OUT_SOURCE.REPLY
          : i === 13
            ? OPT_OUT_SOURCE.TEAMMATE
            : null,
      lifecycleId: lifecycle.id,
      sortOrder: i,
      assignedUserId: assignedUser?.id ?? null,
      lastMessageProcessedAt,
      lastBumpedAt: lastMessageProcessedAt,
      lastMessageOwner: pickRandom(["contact", "user"]),
      lastMessageId: createId(),
      createdAt,
      updatedAt: createdAt,
      tags: contactTags.map((t) => t.id),
      treatmentIds: [],
      productIds: [],
      adIds: [],
      websiteIds: [],
      channels: contactChannels,
      // A fifth stay null so the "Not recorded" row is reachable in mock mode.
      origin: randomBool(0.8) ? pickRandom([...ORIGINS]) : null,
      referredByName: null,
      assignedUser: assignedUser,
      interactions: assignedUser
        ? [
            {
              userId: assignedUser.id,
              interactedAt: new Date(createdAt),
              contactId,
              unreadCount: randomInt(0, 5),
            },
          ]
        : [],
      status: isFirstContact ? "open" : status, // First contact is always open
      snoozedUntil: isFirstContact ? null : snoozedUntil,
      followUp: randomBool(0.15)
        ? {
            id: createId(),
            reason: randomBool(0.3) ? faker.lorem.sentence() : null,
            dueAt: faker.date.soon({ days: 7 }).toISOString(),
            source: "user" as const,
            reminderSentAt: null,
          }
        : null,
      unreadCount: randomInt(0, 8),
      unreadMentionCount: 0,
      lastSummary: randomBool(0.6) ? faker.lorem.sentence() : null,
      lastNote: noteText,
      lastNoteAt: noteAt,
      lastAd: randomBool(0.35) ? generateLastAd() : null,
      lastWebsiteVisit: randomBool(0.3)
        ? generateLastWebsiteVisit(market)
        : null,
      value: faker.number
        .float({ min: 0, max: 10000, fractionDigits: 2 })
        .toFixed(2),
      humanAgentRequired: false,
      knowledgeGapPending: false,
      aiPending: null,
      assignedTeamId: null,
      // Echo's modes, spread across the list so the inbox card can be seen in
      // each: every fifth contact Echo sends on, another drafts, and one is
      // paused.
      aiMode: i % 5 === 1 ? "sends" : i % 5 === 3 ? "drafts" : "off",
      aiModeUntil:
        i === 6 ? new Date(Date.now() + 2 * 3_600_000).toISOString() : null,
      aiModeRevertTo: i === 6 ? "sends" : null,
      // ClinicSoft assigns an MRN some time after a patient is created, so
      // roughly a third of contacts stay blank to keep the empty state visible.
      dynamicValues:
        i % 3 === 0
          ? {}
          : {
              mrn: 7000 + i,
              insurer: INSURERS[market][i % INSURERS[market].length],
            },
      ...(isEcommerce ? generateOrderFields() : generateVisitFields()),
    });

    // Track relationships
    contactLifecycleMap.set(contactId, lifecycle.id);
    contactTagMap.set(
      contactId,
      contactTags.map((t) => t.id),
    );
  }

  // Update lifecycle contact counts
  lifecycles.forEach((lifecycle) => {
    lifecycle.contactCount = contacts.filter(
      (c) => c.lifecycleId === lifecycle.id,
    ).length;
  });

  return { contacts, contactLifecycleMap, contactTagMap };
}

/**
 * Get weighted lifecycle index based on distribution
 */
function getWeightedLifecycleIndex(
  index: number,
  total: number,
  weights: number[],
): number {
  const cumulative: number[] = [];
  let sum = 0;

  for (const weight of weights) {
    sum += weight;
    cumulative.push(sum);
  }

  const position = (index / total) * 100;

  for (let i = 0; i < cumulative.length; i++) {
    if (position < cumulative[i]) {
      return i;
    }
  }

  return weights.length - 1;
}
